// FIRMAS SOBRE EL PDF ORIGINAL, COMO DOCUSIGN (Lissette 2026-10-23).
//
// La agente marca UNA vez los sitios del PDF donde el cliente tiene que firmar,
// poner iniciales, su nombre o la fecha; el cliente da cada cosa UNA vez y el
// servidor la estampa en todos esos sitios de las páginas originales.
//
// Cada sitio es una caja en FRACCIONES de la página tal como se ve (0..1, desde
// arriba a la izquierda): da igual a qué tamaño la pinte cada pantalla.
//
//   { id, tipo: 'firma'|'iniciales'|'nombre'|'fecha', page (1..), x, y, w, h }
//
// Gemelo de deskte-api/src/utils/camposSobreElPdf.ts, que es quien MANDA (vuelve
// a limpiar todo al guardar y al firmar). Una prueba exige que la lista de tipos
// sea la misma en los dos.

export const TIPOS_DE_CAMPO_PDF = ['firma', 'iniciales', 'nombre', 'fecha'];

/** Mismo tope que el servidor. */
export const MAX_CAMPOS_PDF = 100;
const LADO_MINIMO = 0.005;

/** Cómo se llama y de qué color se pinta cada tipo, en las dos pantallas. */
export const ETIQUETAS_DE_CAMPO_PDF = {
  firma: { es: 'Firma', en: 'Signature', color: '#00a8d6' },
  iniciales: { es: 'Iniciales', en: 'Initials', color: '#a855f7' },
  nombre: { es: 'Nombre y apellido', en: 'Full name', color: '#10b981' },
  fecha: { es: 'Fecha', en: 'Date', color: '#f59e0b' },
};

/** Tamaño con el que nace una caja nueva, en PUNTOS PDF (1/72 de pulgada). */
export const TAMANO_POR_DEFECTO = {
  firma: { w: 170, h: 30 },
  iniciales: { w: 50, h: 22 },
  nombre: { w: 170, h: 18 },
  fecha: { w: 80, h: 18 },
};

const redondear = (n) => Math.round(n * 10000) / 10000;

/** Deja la caja DENTRO de la página, con el mismo criterio que el servidor. */
export function limitarCaja(c) {
  let x = Math.min(Math.max(Number(c.x) || 0, 0), 1 - LADO_MINIMO);
  let y = Math.min(Math.max(Number(c.y) || 0, 0), 1 - LADO_MINIMO);
  const w = Math.min(Math.max(Number(c.w) || 0, LADO_MINIMO), 1 - x);
  const h = Math.min(Math.max(Number(c.h) || 0, LADO_MINIMO), 1 - y);
  return { ...c, x: redondear(x), y: redondear(y), w: redondear(w), h: redondear(h) };
}

let contador = 0;
/** Un id corto y único dentro de la lista. */
export function idDeCampo(tipo = 'c') {
  contador += 1;
  return `${tipo.slice(0, 3)}_${Date.now().toString(36)}${contador}`;
}

/**
 * Una caja nueva de `tipo`, centrada en el punto (fx, fy) de la página `page`.
 * `ancho`/`alto` son las medidas de la página en puntos, para que la caja nazca
 * con un tamaño de papel de verdad (una firma de 6 cm, no "un tercio de hoja").
 */
export function nuevoCampo(tipo, page, fx, fy, ancho = 612, alto = 792) {
  const t = TAMANO_POR_DEFECTO[tipo] || TAMANO_POR_DEFECTO.firma;
  const w = t.w / ancho;
  const h = t.h / alto;
  return limitarCaja({ id: idDeCampo(tipo), tipo, page, x: fx - w / 2, y: fy - h / 2, w, h });
}

/** Cuántos sitios hay de cada tipo — para el resumen de la plantilla. */
export function cuentaPorTipo(campos) {
  const out = { firma: 0, iniciales: 0, nombre: 0, fecha: 0 };
  for (const c of Array.isArray(campos) ? campos : []) if (out[c.tipo] !== undefined) out[c.tipo] += 1;
  return out;
}

/** ¿Se solapan dos cajas de la misma página más de `umbral` (sobre la menor)? */
export function seSolapan(a, b, umbral = 0.3) {
  if (a.page !== b.page) return false;
  const ix = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
  const iy = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
  const menor = Math.min(a.w * a.h, b.w * b.h) || 1;
  return (ix * iy) / menor > umbral;
}

/** ¿Es un PDF este archivo o este tipo MIME? */
export function esPdf(archivoOMime) {
  if (!archivoOMime) return false;
  if (typeof archivoOMime === 'string') return archivoOMime.toLowerCase().includes('pdf');
  return String(archivoOMime.type || '').toLowerCase().includes('pdf') || /\.pdf$/i.test(String(archivoOMime.name || ''));
}

/** El nombre legible del PDF a partir del blob (`agencia/docs/<id>-<nombre>`). */
export function nombreDelPdf(blobName) {
  const ultimo = String(blobName || '').split('/').pop() || '';
  return ultimo.replace(/^[A-Za-z0-9_-]{16}-/, '') || ultimo;
}
